<script>
import helperService from "@/services/helper";
import patientService from "@/services/patient";
import appointmentService from "@/services/appointment";
import { getMedicalInsuranceNumber } from "@/services/patient-utils";
import {
  APPOINTMENTS_TYPES,
  VIDEOROOM_MM_OFFSET_PRE,
  VIDEOROOM_MM_OFFSET_POST,
} from "@/services/constants";
import { format, addMilliseconds } from "date-fns";
import pagination from "@/mixins/pagination";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import institutionService from "@/services/institution";
import {
  isVideoRoomEnabled,
  isAgendaVirtualEnabled,
  isManualManagementEnabled,
  isAdmissionForDeragopyanEnabled,
} from "@/services/config";
import { mapGetters } from "vuex";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "ModalAppointment",

  mixins: [pagination],

  components: {
    Spinner,
  },

  props: {
    showDialogModal: {
      type: Boolean,
      default: false,
    },
    selectedSlot: {
      type: Object,
    },
    selectedAppointment: {
      type: Object,
    },
    selectedMedicalSpecialty: {
      type: Object,
    },
  },

  data() {
    return {
      appointmentTypes: APPOINTMENTS_TYPES,
      attentionTypes: [],
      patients: [],
      searchInput: "",
      selectedPatient: null,
      appointment: {
        slot: this.selectedSlot,
        patient: null,
        medicalSpecialty: this.selectedMedicalSpecialty?._id,
        institution: this.selectedSlot.institution,
        description: "",
        status: "",
        attentionType: "",
        videoRoom: null,
      },
      institution: null,
      isNewAppointment: !this.selectedSlot.appointment,
      isPosting: false,
      isDeleting: false,
      isLoading: false,
      customStartTime: new Date(),
      customDuration: 15,
      isManualManagementEnabled,
      isAdmissionForDeragopyanEnabled,
    };
  },

  computed: {
    ...mapGetters(["isPatient", "user"]),
    isInstantAppointment() {
      return !this.selectedSlot._id;
    },

    isVideoRoomAvailable() {
      if (!this.appointment) {
        return false;
      }

      const now = new Date();

      const offsetStartsAt = new Date(this.appointment.startsAt);
      const offsetEndsAt = new Date(this.appointment.endsAt);

      offsetStartsAt.setMinutes(
        offsetStartsAt.getMinutes() - VIDEOROOM_MM_OFFSET_PRE
      );
      offsetEndsAt.setMinutes(
        offsetEndsAt.getMinutes() + VIDEOROOM_MM_OFFSET_POST
      );

      return (
        this.appointment.attentionType === "virtual" &&
        offsetStartsAt <= now &&
        offsetEndsAt >= now &&
        isVideoRoomEnabled
      );
    },

    canCancelAppointment() {
      if (this.isPatient) {
        return this.appointment.patient === this.user._id;
      } else {
        return true;
      }
    },
  },

  created() {
    if (this.isNewAppointment) {
      !this.isPatient && this.getPatients();
      this.appointment.status = "pending";
    } else {
      if (!this.isNewAppointment) {
        this.getAppointment();
      }
    }
    this.getInstitution();
    this.setAttentionTypes();
  },

  methods: {
    async getPatients(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...query,
      };

      patientService
        .get(defaultQuery)
        .then((res) => {
          this.patients = res.docs;
          this.pagination.total = res.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceInput: debounce(function () {
      const query = {
        offset: 0,
        limit: this.pagination.limit,
      };

      const input = this.searchInput && this.searchInput.split(" ");
      const useSearchIndex = input && (input.length === 1 || input.length > 3);

      // Search up to 3 words (Double names or double last names)
      const useNameIndex = input && input.length > 1 && input.length <= 3;

      if (useSearchIndex) {
        query.$text = {
          $search: this.searchInput,
        };
      } else if (useNameIndex) {
        Object.assign(query, helperService.createNameQuery(this.searchInput));
      }

      this.getPatients(query);
    }, DEBOUNCE_MS_TIMEOUT),

    async getAppointment() {
      this.appointment = await appointmentService.getAppointmentById({
        id: this.selectedSlot.appointment?._id || this.selectedSlot.appointment,
        params: { populate: ["institution", "medicalSpecialty"] },
      });
      await this.getPatientById();
    },

    async getInstitution() {
      this.institution = await institutionService.getInstitutionById(
        this.selectedSlot.institution?._id || this.selectedSlot.institution
      );
    },

    async getPatientById() {
      const patient = await patientService.getById(this.appointment.patient);
      this.selectedPatient = patient;
    },

    setAttentionTypes() {
      this.selectedSlot.attentionType.forEach((type) => {
        if (!isAgendaVirtualEnabled && type === "virtual") return;
        this.attentionTypes.push(APPOINTMENTS_TYPES[type]);
      });
    },

    selectPatient(patient) {
      this.selectedPatient = patient;
      this.appointment.patient = patient._id;
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },

    getClassName(patient) {
      if (this.selectedPatient?._id === patient._id) {
        return "is-active";
      } else {
        return "";
      }
    },

    async createAppointment() {
      if (
        !isAgendaVirtualEnabled &&
        this.appointment.attentionType === "virtual"
      ) {
        this.$message.error("No tiene permisos para crear turnos virtuales");
        return;
      }

      const isInstant = !this.selectedSlot._id;

      if (isInstant && this.customStartTime) {
        const startDateTime = new Date(this.customStartTime);
        const endDateTime = new Date(
          startDateTime.getTime() + this.customDuration * 60000 - 1
        );

        const updatedSlot = {
          ...this.selectedSlot,
          startsAt: startDateTime.toISOString(),
          endsAt: endDateTime.toISOString(),
        };
        this.appointment.slot = updatedSlot;
      }

      this.isPosting = true;
      try {
        let appointment = await appointmentService.createAppointment({
          ...this.appointment,
          patient: this.isPatient ? this.user._id : this.appointment.patient,
        });

        if (isInstant && this.isAdmissionForDeragopyanEnabled) {
          const now = new Date();
          const appointmentStart = new Date(appointment.startsAt);

          if (appointmentStart <= now) {
            appointment = await appointmentService.startAppointment(
              appointment._id
            );
          }
        }

        this.$bus.$emit("appointment-created", this.selectedSlot, appointment);
        this.$message.success("Turno asignado con éxito");
      } catch (error) {
        console.log(error);
      }

      this.isPosting = false;
      this.close();
    },

    cancelAppointment() {
      this.$confirm(`¿Desea cancelar la cita?`, "Cancelar", {
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
        type: "warning",
      })
        .then(() => {
          this.isDeleting = true;
          appointmentService
            .cancelAppointment(this.appointment._id)
            .then((cancelledAppointment) => {
              this.$bus.$emit("appointment-cancelled", cancelledAppointment);
              this.$message.success("Turno cancelado con éxito");
              this.isDeleting = false;
              this.close();
            });
        })
        .catch(() => {});
    },

    getStartAndEndHours(slot) {
      const parsedStartHour = format(new Date(slot.startsAt), "HH:mm");

      let parsedEndHour = format(
        addMilliseconds(new Date(slot.endsAt), 1),
        "HH:mm"
      );

      // workaround
      if (parsedEndHour === "00:00") {
        parsedEndHour = "24:00";
      }
      // workaround

      return `${parsedStartHour} - ${parsedEndHour}`;
    },

    async goToPatientVideoRoom() {
      this.$router.push({
        name: "appointment",
        params: { appointmentId: this.appointment._id },
      });
    },

    getMedicalInsuranceNumber,

    getMedicalSpecialty() {
      return this.isNewAppointment
        ? this.selectedMedicalSpecialty?.name
        : this.appointment?.medicalSpecialty?.name ||
            this.selectedAppointment?.medicalSpecialty?.name;
    },

    submit() {
      this.createAppointment();
    },

    close() {
      this.$emit("close");
    },
  },
};
</script>

<template lang="pug">
el-dialog(:visible.sync="showDialogModal", :before-close="close")
    ValidationObserver(v-slot="{handleSubmit}")
      form.modal(@submit.prevent="handleSubmit(submit)")
        header.modal__header(v-if='isNewAppointment')
          h2.modal__title Agendar nueva cita
          .modal__actions
            el-button(type="info" @click="close()" :disabled='isPosting') Cancelar
            el-button.border(type="primary" native-type="submit" :loading="isPosting") Agendar
        header.modal__header(v-else)
          h2.modal__title Cita asignada
          .modal__actions
            el-button.border(type="primary" @click="close()" :disabled='isPosting') Cerrar
        .modal__content
          .modal__block
            .modal__section
              .modal__sign.sign
                .sign__icon
                  micon(name="calendar")
                h3.sign__title Datos de la cita
              article.modal__fields
                .modal__row(v-if='!isInstantAppointment')
                  fieldset.modal__field
                    label.label Fecha
                    p {{ selectedSlot.startsAt | formatDate}}
                  fieldset.modal__field
                    label.label Horario
                    p {{ getStartAndEndHours(selectedSlot)}}
                  fieldset.modal__field
                    el-button.room-button(v-if='isVideoRoomAvailable' type="info" :loading="isPosting" @click='goToPatientVideoRoom') Ingresar a la sala
                
                .modal__row(v-if='isInstantAppointment')
                  fieldset.modal__field
                    label.label * Fecha y Horario
                    ValidationProvider(name="Fecha y Hora", rules="required|includeToday", v-slot="{ errors }")
                      el-date-picker(
                        v-model="customStartTime",
                        type="datetime",
                        placeholder="Seleccione fecha y hora",
                        format="dd/MM/yyyy HH:mm",
                        lang="es",
                        :not-before="new Date()"
                      )
                      span.has-error {{ errors[0] }}
                  
                  fieldset.modal__field
                    label.label Duración (minutos)
                    ValidationProvider(name="Duración", rules="required|integer|min_value:5", v-slot="{ errors }")
                      el-input(
                        v-model="customDuration",
                        type="number"
                      )
                      span.has-error {{ errors[0] }}
                .modal__row    
                  fieldset.modal__field
                    label.label Doctor
                    p {{ getFullName(selectedSlot.doctor) || getFullName(selectedAppointment.doctor) }}
                  fieldset.modal__field
                    label.label Especialidad
                    p {{ getMedicalSpecialty() }}
                  fieldset.modal__field
                    label.label Lugar
                    p(v-if='institution') {{ institution.name }} - {{ institution.address.street }} {{institution.address.buildingNumber}}, {{ institution.address.district }} {{institution.address.postalCode }}, {{ institution.address.district }}, {{ institution.address.city }} {{ institution.address.apartmentFloor ? `, Piso ${institution.address.apartmentFloor}` : "" }} {{ institution.address.apartmentNumber ? `, Número ${institution.address.apartmentNumber}` : "" }}  
                
                .modal__row(v-if='isNewAppointment')
                  fieldset.modal__field
                    label.label * Modalidad
                    ValidationProvider(name="Modalidad", rules="required", v-slot="{ errors }")
                      el-select(
                        v-model="appointment.attentionType",
                        placeholder='Seleccione el tipo de cita'
                        required
                      )
                        el-option(
                          v-for="type in attentionTypes",
                          :key="type.code",
                          :label="type.friendlyName",
                          :value="type.value"
                        )
                      span.has-error {{ errors[0] }} 

                .modal__row(v-else)
                  fieldset.modal__field
                      label.label Modalidad
                      p(v-if='appointment.attentionType') {{ appointmentTypes[appointment.attentionType].friendlyName }}
                div(v-if='isNewAppointment')
                  .modal__row
                    fieldset.modal__field
                      label.label * Paciente
                      ValidationProvider(name="Paciente", rules="required", v-slot="{ errors }")
                        el-input(
                          :value="isPatient? getFullName(user) : getFullName(selectedPatient)"
                          placeholder="Seleccione un paciente"
                          readOnly
                        )
                        span.has-error {{ errors[0] }} 

                  .box.box_table(v-if='isNewAppointment && !isPatient' :class='selectedPatient ? "selected" : ""')
                    el-input.search__input(
                      prefix-icon="el-icon-search"
                      v-on:input="debounceInput"
                      v-model="searchInput"
                      placeholder="Buscar"
                      :clearable="true"
                    )
                    .box__content--stretch
                      table
                        thead
                          tr
                            th Paciente
                            th Documento
                            th Número de Socio

                        tbody
                          spinner(v-if="isLoading")
                          tr(v-else v-for="(patient, index) in patients" :key="patient._id" :class="getClassName(patient)" @click="selectPatient(patient)")
                            td
                              b {{ getFullName(patient) }}
                            td {{ patient.governmentId && patient.governmentId.number }}
                            td {{ getMedicalInsuranceNumber(patient) }}

                    pagination(
                      :isLoading="isLoading"
                      :limit="pagination.limit"
                      :total="pagination.total"
                      @pagination="setPagination"
                    )      

                  .modal__row.patientData(v-if='selectedPatient')
                    fieldset.modal__field
                      label.label Mail
                      p {{ selectedPatient.email }}
                    fieldset.modal__field
                      label.label Teléfono
                      p {{ selectedPatient.phone }}

                .modal__row.cancel-button(v-if='isManualManagementEnabled && !isNewAppointment && canCancelAppointment')
                  fieldset.modal__field
                    el-button(type="info" :loading="isPosting" @click='cancelAppointment') Cancelar cita
</template>

<style lang="scss" scoped>
.label {
  position: relative;
  .search-other {
    position: absolute;
    right: 0;
    top: 0;
  }
}

.selected {
  height: auto;
}

.box_table {
  margin: 1rem 0.8rem 0;
}

.cancel-button {
  margin-top: 2rem;

  button {
    border: none;
    background-color: #c0392b;
  }

  button:hover {
    background-color: #892a1f;
  }
}

.room-button {
  border: none;
  background-color: rgb(113, 215, 135);
}

.is-active {
  background-color: $primary;
  color: $white;
}

.is-active:hover,
.is-active:hover td {
  background-color: $primary !important;
}

.patientData {
  margin-top: 1.5rem;
}

.notAvailable {
  text-align: center;
  margin-top: 1.5rem;
}
</style>
